import { useEffect, useMemo, useState } from "react";
import pluralize from "pluralize";
import { Button, ButtonGroup, FormGroup, Card, Input } from "reactstrap";
import { SelectPicker, Tooltip, Whisper } from "rsuite";
import _ from "underscore";

import AppDrawer from "components/Drawers/AppDrawer";
import useModule from "hooks/useModule";
import useFieldMap from "hooks/useFieldMap";
import SettingsGroup from "components/CRM/SettingsGroup";
import WidgetRenderer from "../WidgetRenderer";
import NameDescriptionGroup from "./NameDescriptionGroup";
import FiltersGroup from "./FiltersGroup";
import { sanitizeWidget } from "utils/DashboardUtils";
import { timeFrameData } from "utils/DashboardUtils";
import ModulePicker from "components/Modules/ModulePicker";
import StrUtils from "utils/StrUtils";
import FieldUtils from "utils/FieldUtils";
import AppColorPicker from "elements/AppColorPicker";
import SortStages from "./SortStages";
import { AppConfirm } from "utils/PopupUtil";
import InfoToolip from "elements/InfoToolip";
import InputFormatter from "components/Records/Formatters/InputFormatter";
import AppSpinner from "../../../elements/AppSpinner";
import Constants from "../../../utils/Constants";
import GroupedBarChartStyles from "./WidgetStyleOptions/GroupedBarChartStyles";

export const ChartTypesMap = {
  PieChart: {
    icon: "chart-pie",
    label: "Pie Chart",
  },
  FilledLineChart: {
    icon: "chart-areaspline-variant",
    label: "Filled Line Chart",
  },
  DonutChart: {
    icon: "chart-donut",
    label: "Donut Chart",
  },
  BarChart: {
    icon: "chart-bar",
    label: "Bar Chart",
  },
  GroupedBarChart: {
    icon: "chart-bar-stacked",
    label: "Grouped Bar Chart",
  },
  ProgressBarChart: {
    icon: "glass-stange",
    label: "Progress Bar Chart",
  },
  LineChart: {
    icon: "chart-line",
    label: "Line Chart",
  },
  GroupedLineChart: {
    icon: "chart-line-stacked",
    label: "Grouped Line Chart",
  },
  StageFunnel: {
    icon: "stairs",
    label: "Stage Funnel",
  },
};

const NoDataSource = () => (
  <h5 className="my-2 text-center">Select A Data Source</h5>
);

const ChartSettings = ({
  open,
  onClose,
  setSettings,
  section,
  updateWidget,
  isUpdating,
  record,
  theme,
  insightsModule,
  topLevelFilters = undefined,
}) => {
  const [prevWidget, setPrevWidget] = useState(null);
  const [widget, setWidget] = useState(JSON.parse(JSON.stringify(section)));
  const { module, getModule } = useModule(widget?.ModuleAPIName);
  const [dynamicValues, setDynamicValues] = useState(false);
  const fieldTypeMap = useFieldMap(widget?.ModuleAPIName, "FieldType");
  const isSingleStat = useMemo(
    () => widget?.Type === "SingleStat",
    [widget?.Type]
  );

  const AvailableFields = (fields, moduleAPIName, referenceField) => {
    let groupByFields = [];
    const valueFields = [];
    const referenceName = referenceField?.Name ?? "";
    const referenceAPIName = referenceField?.APIName ?? "";

    for (let f of fields) {
      const fieldName = `${referenceName ? `${referenceName} - ` : ""}${
        f.Name
      }`;
      const fieldAPIName = `${referenceAPIName ? `${referenceAPIName}.` : ""}${
        f.APIName
      }`;

      if (
        f.FieldType === "SingleReference" ||
        f.FieldType === "MultiReference" ||
        f.FieldType === "SingleSelect" ||
        f.FieldType === "MultiSelect" ||
        f.FieldType === "DateTime" ||
        f.FieldType === "Date" ||
        f.FieldType === "Text" ||
        f.FieldType === "Number" ||
        f.FieldType === "Percent" ||
        f.FieldType === "Currency" ||
        f.FieldType === "CurrencyInterval" ||
        f.FieldType === "Calculated" ||
        f.FieldType === "Toggle" ||
        f.FieldType === "Location"
      ) {
        if (f.FieldType === "Location") {
          groupByFields.push({
            label: `${fieldName} - City`,
            value: `${fieldAPIName}.Address.City`,
          });

          groupByFields.push({
            label: `${fieldName} - State`,
            value: `${fieldAPIName}.Address.StateProvince`,
          });
          groupByFields.push({
            label: `${fieldName} - Country`,
            value: `${fieldAPIName}.Address.Country`,
          });
          continue;
        }

        // Push base group before adding the Lookup fields
        groupByFields.push({ label: fieldName, value: fieldAPIName });

        if (
          f.FieldType === "SingleReference" &&
          !referenceField &&
          widget?.Type !== "GroupedBarChart" &&
          widget?.Type !== "GroupedLineChart"
        ) {
          const sourceList = f.SourcesList;

          if (!sourceList) continue;

          const modAPIName = (
            typeof sourceList === "string" ? sourceList : sourceList[0]
          ).replace("Tenant.Modules.", "");

          const referencedModule = getModule(modAPIName);

          if (!referencedModule) continue;

          const groupedResult = AvailableFields(
            referencedModule?.Fields,
            moduleAPIName,
            f
          );

          groupByFields = [...groupByFields, ...groupedResult.groupBy];
        }
      }

      if (
        (f.FieldType === "Number" ||
          f.FieldType === "Percent" ||
          f.FieldType === "Currency" ||
          f.FieldType === "CurrencyInterval" ||
          f.FieldType === "Calculated") &&
        !referenceField
      ) {
        valueFields.push({ label: fieldName, value: fieldAPIName });
      }
    }

    groupByFields.push({
      label: `${referenceName ? `${referenceName} - ` : ``}Tags`,
      value: `${referenceAPIName ? `${referenceAPIName}.` : ``}Tags`,
    });
    return { groupBy: groupByFields, value: valueFields };
  };

  const stages = useMemo(() => {
    if (!module?.Fields) return [];

    const filteredFields = module.Fields.filter(
      (field) => field.FieldType === "SingleSelect"
    );

    const stages = filteredFields.map((stage) => {
      return {
        label: stage.Name,
        value: stage.APIName,
      };
    });

    return stages;
  }, [module?.Fields]);

  const mappedFields = useMemo(() => {
    if (!module?.Fields?.length) return { groupBy: [], value: [] };
    const groupByFields = [];
    const valueFields = [];

    const result = AvailableFields(module?.Fields, module?.APIName);

    return { groupBy: result.groupBy, value: result.value };
  }, [module?.Fields]);

  useEffect(() => {
    if (
      section?.Target &&
      typeof section.Target === "string" &&
      section.Target.includes("{{")
    ) {
      setDynamicValues(true);
    }
    if (prevWidget) {
      setWidget({ ...prevWidget });
    } else {
      setWidget(JSON.parse(JSON.stringify(section)));
    }
  }, [section, prevWidget]);

  const handleSave = () => {
    if (JSON.stringify(widget) === JSON.stringify(section)) {
      return onClose();
    }

    sanitizeWidget(widget);

    updateWidget(widget, onClose);
  };

  const updateSection = (prop, value) => {
    setWidget((prev) => {
      prev[prop] = value;
      return { ...prev };
    });
  };

  const updateSectionModule = (newModule) => {
    updateSection("ModuleAPIName", newModule);

    let newQuery = {
      Fields: ["*"],
      Filters: [],
      Aggregations: [{ Method: "", Field: "" }],
      GroupBy: { Field: "" },
      BenchmarkLines: [],
    };

    if (
      widget?.Type === "GroupedBarChart" ||
      widget?.Type === "GroupedLineChart"
    ) {
      newQuery = {
        Aggregations: [{ Method: "", Field: "" }],
        GroupBy: [{ Field: "" }, { Field: "" }],
      };
    }

    updateSection("Query", newQuery);
  };

  const updateXAxis = (newField) => {
    const newQuery = { ...widget.Query };

    // Array position 1 is the X-Axis group by field.
    if (widget?.GroupBy?.length < 1) {
      newQuery.GroupBy = [{}, { Field: newField }];
    } else if (widget?.GroupBy?.length === 1) {
      newQuery.GroupBy.push({ Field: newField });
    } else {
      newQuery.GroupBy[1] = { Field: newField };
    }

    updateSection("Query", newQuery);
  };

  const updateGroupBy = (newField) => {
    const newQuery = widget?.Query
      ? { ...widget.Query }
      : {
          Fields: ["*"],
          Filters: [],
          Aggregations: [{ Method: "", Field: "" }],
          BenchmarkLines: [],
        };

    // Handle Multi Group By cases. Array position 0 is the group by field.
    if (
      widget?.Type === "GroupedBarChart" ||
      widget?.Type === "GroupedLineChart"
    ) {
      if (widget?.GroupBy?.length < 1) {
        newQuery.GroupBy = [{ Field: newField }];
      } else {
        newQuery.GroupBy[0] = { Field: newField };
      }
    } else {
      newQuery.GroupBy = { Field: newField };
    }

    if (newField?.includes(".")) {
      const splitField = newField.split(".");

      const fieldType = fieldTypeMap[splitField[0]];

      if (fieldType === "SingleReference") {
        newQuery.Fields.push(newField);
      }
    } else {
      newQuery.Fields = ["*"];
    }

    updateSection("Query", newQuery);
  };

  const updateInterval = (newTimeUnit) => {
    const newQuery = { ...widget?.Query };

    if (
      widget?.Type === "GroupedBarChart" ||
      widget?.Type === "GroupedLineChart"
    ) {
      newQuery.GroupBy[1].Interval = newTimeUnit;
    } else {
      newQuery.GroupBy.Interval = newTimeUnit;
    }
    updateSection("Query", newQuery);
  };

  const updateAggregation = (val, prop) => {
    const newQuery = widget?.Query
      ? { ...widget.Query }
      : {
          Fields: ["*"],
          Filters: [],
          Aggregations: [{ Method: "", Field: "" }],
          BenchmarkLines: [],
        };

    newQuery.Aggregations[0][prop] = val;

    updateSection("Query", newQuery);
  };

  const updateChartColor = (newChartColor) => {
    updateAggregation(newChartColor, "ChartColor");
  };

  const updateTextColor = (newTextColor) => {
    updateAggregation(newTextColor, "TextColor");
  };

  const handleClose = async () => {
    // Save the widget in case the user wants to continue editing.
    const widgetSnapshot = widget;
    onClose();
    setPrevWidget(null);

    if (JSON.stringify(widget) === JSON.stringify(section)) return;

    const result = await AppConfirm({
      title: "Unsaved Changes",
      text: "You have made changes to the settings. Do you want to save them?",
      showDenyButton: true,
      confirmButtonColor: "var(--primary)",
      confirmButtonText: "Save Changes",
      denyButtonColor: "var(--danger)",
      denyButtonText: "Discard Changes",
      cancelButtonColor: "var(--secondary)",
      cancelButtonText: "Continue Editing",
    });

    if (result.isDenied) {
      return;
    }
    if (result.isConfirmed) {
      return handleSave();
    }

    setPrevWidget(widgetSnapshot);
    setSettings(section);
  };

  const addBenchmarkLine = () => {
    const defaultColor = "#5B8FF9";

    const newBenchmarkLine = {
      id: StrUtils.uuidv4(),
      Label: "",
      Value: "",
      Color: defaultColor,
    };

    const newQuery = widget?.Query
      ? { ...widget.Query }
      : {
          Fields: ["*"],
          Filters: [],
          Aggregations: [{ Method: "", Field: "" }],
          BenchmarkLines: [],
        };

    if (!newQuery.BenchmarkLines?.length) {
      newQuery.BenchmarkLines = [];
    }

    newQuery.BenchmarkLines.push(newBenchmarkLine);

    updateSection("Query", newQuery);
  };

  const updateBenchmarkLines = (name, value, id) => {
    const newQuery = { ...widget?.Query };
    const foundBenchmarkLine = _.findIndex(newQuery.BenchmarkLines, { id: id });

    newQuery.BenchmarkLines[foundBenchmarkLine][name] = value;

    updateSection("Query", newQuery);
  };

  const removeBenchmarkLine = (id) => {
    const newQuery = { ...widget?.Query };
    const updatedBenchmarkLines = _.filter(
      newQuery.BenchmarkLines,
      (benchmarkL, i) => {
        return benchmarkL.id !== id;
      }
    );

    newQuery.BenchmarkLines = updatedBenchmarkLines;

    updateSection("Query", newQuery);
  };

  const handleDynamicInputChange = (newValue) => {
    updateSection("Target", newValue);
  };

  const ChartButton = ({ chartIcon, chartType, label }) => (
    <Whisper
      placement="autoVertical"
      trigger="hover"
      speaker={<Tooltip>{label}</Tooltip>}
    >
      <Button
        className="btn-icon-only border mb-2"
        style={{ marginLeft: 0 }}
        outline={widget?.Type !== chartType}
        color={widget?.Type === chartType ? "primary" : "secondary"}
        onClick={() => updateSection("Type", chartType)}
      >
        <i className={`mdi mdi-${chartIcon}`} />
      </Button>
    </Whisper>
  );

  const xAxisGroupBy = widget?.Query?.GroupBy?.[1]?.Field ?? null;
  const groupByField =
    widget?.Query?.GroupBy?.Field ?? widget?.Query?.GroupBy?.[0]?.Field;

  const aggregationField = widget?.Query?.Aggregations?.[0]?.Field;
  const interval =
    widget?.Query?.GroupBy?.Interval ?? widget?.Query?.GroupBy?.[1]?.Interval;

  const options = useMemo(() => {
    if (!record?.id) return;
    const recordModule = getModule(record._ModuleAPIName);
    if (!recordModule?.Fields?.length || !record?.id) return [];
    const numberOptions = recordModule?.Fields?.map((f) => {
      if (f.FieldType === "Number" && record[f?.APIName]) {
        return {
          label: f?.Name,
          value: `{{${f.APIName}}}`,
        };
      }

      return;
    }).filter((option) => option !== undefined);

    return numberOptions;
  }, [module?.Fields, getModule]);

  return (
    <AppDrawer
      size="full"
      open={open}
      onClose={handleClose}
      placement="bottom"
      headerTitle={`${widget?.Name} Settings`}
      bodyStyle={{ padding: 0 }}
      headerOptions={
        <Button
          color="primary"
          size="sm"
          onClick={handleSave}
          style={{ width: 100 }}
          disabled={isUpdating}
        >
          {isUpdating ? <AppSpinner color="white" /> : "Save"}
        </Button>
      }
      body={
        <div className="d-flex" style={{ height: "100%" }}>
          <div
            style={{
              width: "calc(100% - 350px)",
            }}
            className="py-4"
          >
            <WidgetRenderer
              section={widget}
              setSettings={null}
              topLevelFilters={topLevelFilters}
              record={record}
              theme={theme}
            />
          </div>
          <div
            style={{ minWidth: 350, overflowY: "auto", maxWidth: 350 }}
            className="border-left p-4"
          >
            <NameDescriptionGroup
              widget={widget}
              updateSection={updateSection}
              theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
            />
            <SettingsGroup
              title="Chart Type"
              className="mb-2"
              hidden={isSingleStat}
            >
              <h5 className="font-weight-500">Available Charts:</h5>
              <div className="d-flex flex-wrap">
                {Object.entries(ChartTypesMap).map(
                  ([chartType, { icon, label }]) => (
                    <ChartButton
                      key={chartType}
                      chartIcon={icon}
                      label={label}
                      chartType={chartType}
                    />
                  )
                )}
              </div>
            </SettingsGroup>
            {/* <SettingsGroup title="Widget Style" className="mb-2">
              <DefaultWidgetStyles
                widget={widget}
                updateSection={updateSection}
                theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
              />
              {widget?.Type === "GroupedBarChart" && (
                <GroupedBarChartStyles
                  widget={widget}
                  updateSection={updateSection}
                  theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
                />
              )}
            </SettingsGroup> */}
            {widget?.Type === "ProgressBarChart" && (
              <SettingsGroup title="Orientation" className="mb-2">
                <ButtonGroup className="d-flex justify-content-center">
                  <Button
                    size="sm"
                    outline={
                      !!widget?.Orientation && widget?.Orientation !== "Bar"
                    }
                    className="border"
                    onClick={() => updateSection("Orientation", "Bar")}
                  >
                    Bar
                  </Button>
                  <Button
                    size="sm"
                    outline={widget?.Orientation !== "Circle"}
                    className="border"
                    onClick={() => updateSection("Orientation", "Circle")}
                  >
                    Circle
                  </Button>
                </ButtonGroup>
              </SettingsGroup>
            )}
            {/* Temporarily disable this group */}
            {/* Readd as a feature down the road */}
            {(widget?.Type === "BarChart" ||
              widget?.Type === "GroupedBarChart" ||
              widget?.Type === "StageFunnel") &&
              false && (
                <SettingsGroup title="Orientation" className="mb-2">
                  <ButtonGroup className="d-flex justify-content-center">
                    <Button
                      size="sm"
                      outline={
                        !!widget?.Orientation &&
                        widget?.Orientation !== "Vertical"
                      }
                      className="border"
                      onClick={() => updateSection("Orientation", "Vertical")}
                    >
                      Vertical
                    </Button>
                    <Button
                      size="sm"
                      outline={widget?.Orientation !== "Horizontal"}
                      className="border"
                      onClick={() => updateSection("Orientation", "Horizontal")}
                    >
                      Horizontal
                    </Button>
                  </ButtonGroup>
                </SettingsGroup>
              )}
            <SettingsGroup title="Data Source" className="mb-2">
              <h5 className="font-weight-500">Data Source:</h5>
              <ModulePicker
                value={widget?.ModuleAPIName}
                onChange={updateSectionModule}
                oneModule={insightsModule}
              />
            </SettingsGroup>
            <SettingsGroup
              title="X-Axis"
              className="mb-2"
              disabled={!widget?.ModuleAPIName}
              disabledComponent={<NoDataSource />}
              hidden={
                widget?.Type !== "GroupedBarChart" &&
                widget?.Type !== "GroupedLineChart"
              }
            >
              <FormGroup className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="font-weight-500 mb-0">Group By:</h5>
                  <InfoToolip
                    text={
                      <>
                        Represents the columns <br />
                        in the chart.
                      </>
                    }
                  />
                </div>
                <SelectPicker
                  preventOverflow
                  data={mappedFields?.groupBy || []}
                  disabled={mappedFields?.groupBy?.length < 1}
                  value={xAxisGroupBy}
                  onChange={updateXAxis}
                  block
                  placeholder="Select field to group by"
                />
              </FormGroup>
              {xAxisGroupBy &&
                (fieldTypeMap[xAxisGroupBy] === "Date" ||
                  fieldTypeMap[xAxisGroupBy] === "DateTime") && (
                  <FormGroup>
                    <h5 className="font-weight-500">Time Frame</h5>
                    <SelectPicker
                      preventOverflow
                      data={timeFrameData || []}
                      value={interval ?? null}
                      onChange={updateInterval}
                      block
                      placeholder="Select a time unit"
                    />
                  </FormGroup>
                )}
            </SettingsGroup>
            <SettingsGroup
              title="Values"
              className="mb-2"
              disabled={!widget?.ModuleAPIName}
              disabledComponent={<NoDataSource />}
              hidden={widget?.Type === "StageFunnel"}
            >
              {!isSingleStat && widget?.Type !== "ProgressBarChart" && (
                <FormGroup>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="font-weight-500 mb-0">Group By:</h5>
                    <InfoToolip
                      text={
                        <>
                          Represents the bar elements <br />
                          in the chart. <br />
                          Supported Field Types: <br />
                          Single/Multi Reference <br />
                          Single/Mutli Select, <br />
                          Date/DateTime
                        </>
                      }
                    />
                  </div>
                  <SelectPicker
                    preventOverflow
                    data={
                      // If it is a GroupedBarChart, filter out any DateTime fields. We only want them for X-Axis.
                      section?.Type === "GroupedBarChart" ||
                      section?.Type === "GroupedLineChart"
                        ? mappedFields?.groupBy.filter((f) => {
                            if (
                              fieldTypeMap[f.label] === "Date" ||
                              fieldTypeMap[f.label] === "DateTime"
                            ) {
                              return false;
                            }

                            return true;
                          })
                        : mappedFields?.groupBy || []
                    }
                    disabled={mappedFields?.groupBy?.length < 1}
                    value={groupByField}
                    onChange={updateGroupBy}
                    block
                    placeholder="Select field to group by"
                  />
                </FormGroup>
              )}

              {groupByField &&
                !xAxisGroupBy && // Disable this feature for GroupedBarChart, as we only want DateTime fields for X-Axis.
                (fieldTypeMap[groupByField] === "Date" ||
                  fieldTypeMap[groupByField] === "DateTime") && (
                  <FormGroup>
                    <h5 className="font-weight-500">Time Frame</h5>
                    <SelectPicker
                      preventOverflow
                      data={timeFrameData || []}
                      value={interval ?? null}
                      onChange={updateInterval}
                      block
                      placeholder="Select a time unit"
                    />
                  </FormGroup>
                )}
              <hr className="my-3"></hr>
              <FormGroup className="m-0">
                <h5 className="font-weight-500">Value:</h5>
                <SelectPicker
                  preventOverflow
                  data={[
                    {
                      label: `Number of ${pluralize(module?.Name ?? "")}`,
                      value: "id",
                    },
                  ].concat(mappedFields?.value || [])}
                  value={aggregationField}
                  onChange={(val) => {
                    updateAggregation(val, "Field");
                    if (widget?.Target) {
                      updateSection("Target", "");
                    }
                    if (val === "id") {
                      updateAggregation("Count", "Method");
                    } else if (
                      val !== "id" &&
                      widget?.Query?.Aggregations?.[0]?.Method === "Count"
                    ) {
                      updateAggregation("", "Method");
                    }
                  }}
                  block
                  placeholder="Select Value"
                />
              </FormGroup>
              {aggregationField && aggregationField !== "id" ? (
                <>
                  <hr className="my-3"></hr>
                  <h5 className="font-weight-500">Calculated By:</h5>
                  <FormGroup>
                    <SelectPicker
                      preventOverflow
                      data={[
                        { label: "Sum", value: "Sum" },
                        { label: "Average", value: "Avg" },
                        { label: "Min", value: "Min" },
                        { label: "Max", value: "Max" },
                      ]}
                      value={widget?.Query?.Aggregations?.[0]?.Method}
                      onChange={(val) => updateAggregation(val, "Method")}
                      block
                      placeholder="Select calculation method"
                    />
                  </FormGroup>
                </>
              ) : (
                <div className="p-2"></div>
              )}
              {widget?.Type === "ProgressBarChart" && (
                <>
                  <hr className="my-3"></hr>
                  <h5 className="font-weight-500">Target</h5>
                  <FormGroup>
                    {aggregationField !== "id" ? (
                      <InputFormatter
                        module={module}
                        field={FieldUtils.getFieldFromModule(
                          module,
                          widget?.Query?.Aggregations?.[0]?.Field
                        )}
                        value={widget?.Target ?? ""}
                        onChange={(f, val) => updateSection("Target", val)}
                      />
                    ) : (
                      <>
                        {dynamicValues && record?.id ? (
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "10px" }}
                          >
                            <SelectPicker
                              placement="autoVertical"
                              data={options}
                              style={{ width: "100%" }}
                              placeholder="Choose A Dynamic Value"
                              onChange={(val) => handleDynamicInputChange(val)}
                              value={widget?.Target ?? ""}
                            />
                            <Button
                              size="sm"
                              className="btn-icon-only"
                              style={{
                                backgroundColor: dynamicValues ? "#f0f3f2" : "",
                                border: dynamicValues
                                  ? "1px solid #bdc9c2"
                                  : "",
                              }}
                              outline
                              onClick={() => setDynamicValues(!dynamicValues)}
                            >
                              <i className="mdi mdi-xml"></i>
                            </Button>
                          </div>
                        ) : (
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "10px" }}
                          >
                            <Input
                              bsSize="sm"
                              type="number"
                              onChange={(e) =>
                                updateSection("Target", e.target.value)
                              }
                              value={widget?.Target ?? ""}
                            />
                            {record?.id && (
                              <Button
                                size="sm"
                                className="btn-icon-only"
                                outline
                                onClick={() => setDynamicValues(!dynamicValues)}
                              >
                                <i className="mdi mdi-xml"></i>
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </FormGroup>
                </>
              )}
            </SettingsGroup>
            <SettingsGroup
              title="Stage"
              className="mb-2"
              hidden={widget?.Type !== "StageFunnel"}
            >
              {stages.length > 0 ? (
                <>
                  <h5 className="font-weight-500">Select Stage:</h5>
                  <FormGroup>
                    <SelectPicker
                      preventOverflow
                      data={stages}
                      value={groupByField}
                      onChange={updateGroupBy}
                      block
                      placeholder="Select field to group as Stage"
                    />
                  </FormGroup>
                  <FormGroup className="mt-3">
                    <h5 className="font-weight-500">Value:</h5>
                    <SelectPicker
                      preventOverflow
                      data={[
                        {
                          label: `Number of ${pluralize(module?.Name ?? "")}`,
                          value: "id",
                        },
                      ].concat(mappedFields?.value || [])}
                      value={aggregationField}
                      onChange={(val) => {
                        updateAggregation(val, "Field");
                        if (val === "id") {
                          updateAggregation("Count", "Method");
                        } else if (
                          val !== "id" &&
                          widget?.Query?.Aggregations?.[0]?.Method === "Count"
                        ) {
                          updateAggregation("", "Method");
                        }
                      }}
                      block
                      placeholder="Select Value"
                    />
                  </FormGroup>
                  {aggregationField && aggregationField !== "id" && (
                    <>
                      <h5 className="font-weight-500">Calculated By:</h5>
                      <FormGroup>
                        <SelectPicker
                          preventOverflow
                          data={[
                            { label: "Sum", value: "Sum" },
                            { label: "Average", value: "Avg" },
                            { label: "Min", value: "Min" },
                            { label: "Max", value: "Max" },
                          ]}
                          value={widget?.Query?.Aggregations?.[0]?.Method}
                          onChange={(val) => {
                            const auxQuery = { ...widget?.Query };
                            auxQuery.Fields = [];
                            updateSection("Query", auxQuery);
                            updateAggregation(val, "Method");
                          }}
                          block
                          placeholder="Select calculation method"
                        />
                      </FormGroup>
                    </>
                  )}
                </>
              ) : (
                <NoDataSource />
              )}
            </SettingsGroup>
            <SettingsGroup
              className="mb-2"
              title="Order Stages"
              hidden={widget?.Type !== "StageFunnel"}
            >
              {stages.length > 0 ? (
                <>
                  <h5>Stages</h5>
                  <SortStages
                    module={module}
                    section={widget}
                    updateSection={updateSection}
                  />
                </>
              ) : (
                <NoDataSource />
              )}
            </SettingsGroup>
            {(widget?.Type === "BarChart" ||
              widget?.Type === "LineChart" ||
              widget?.Type === "FilledLineChart") && (
              <>
                <SettingsGroup title="Sort By" className="mb-2">
                  <ButtonGroup className="d-flex justify-content-center">
                    <Button
                      size="sm"
                      outline={!!widget?.OrderBy}
                      className="border"
                      onClick={() => updateSection("OrderBy", null)}
                    >
                      Don&apos;t Sort
                    </Button>
                    <Button
                      size="sm"
                      outline={widget?.OrderBy !== "ASC"}
                      className="border"
                      onClick={() => updateSection("OrderBy", "ASC")}
                    >
                      Ascending
                    </Button>
                    <Button
                      size="sm"
                      outline={widget?.OrderBy !== "DESC"}
                      className="border"
                      onClick={() => updateSection("OrderBy", "DESC")}
                    >
                      Descending
                    </Button>
                  </ButtonGroup>
                </SettingsGroup>
                <SettingsGroup title="Style" className="mb-2">
                  <AppColorPicker
                    title="Chart Color"
                    containerCSS="my-2"
                    value={widget?.Query?.Aggregations?.[0]?.ChartColor}
                    onChange={(newColor) => updateChartColor(newColor)}
                    theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
                    onClear={() => updateChartColor(undefined)}
                  />
                  {/* <AppColorPicker
                    title="Text Color"
                    containerCSS="mt-3"
                    value={
                      widget?.Query?.Aggregations?.[0]?.TextColor ?? "#ffffff"
                    }
                    onChange={(newColor) => updateTextColor(newColor)}
                  /> */}
                </SettingsGroup>
                <SettingsGroup title="Benchmark Lines" className="mb-2">
                  {widget?.Query?.BenchmarkLines?.map((item) => (
                    <Card
                      className={`border bg-white shadow-none px-3 pb-3 pt-2`}
                      key={item.id}
                    >
                      <div className="d-flex justify-content-end">
                        <Button
                          size="sm"
                          outline
                          color="secondary"
                          className="btn-icon-only"
                          onClick={() => removeBenchmarkLine(item.id)}
                          style={{ height: "1.5rem", width: "1.5rem" }}
                        >
                          <i
                            className="mdi mdi-close"
                            style={{ fontSize: 20 }}
                          />
                        </Button>
                      </div>
                      <h5
                        className="font-weight-500 mb-1"
                        style={{ display: "inline-block" }}
                      >
                        Label
                      </h5>
                      <Input
                        className="mb-3"
                        bsSize="sm"
                        type="text"
                        name="Label"
                        value={item.Label}
                        onChange={(e) =>
                          updateBenchmarkLines(
                            e.target.name,
                            e.target.value,
                            item.id
                          )
                        }
                      />

                      <h5 className="font-weight-500 mb-1">Value</h5>
                      <Input
                        bsSize="sm"
                        type="number"
                        name="Value"
                        value={item.Value}
                        onChange={(e) =>
                          updateBenchmarkLines(
                            e.target.name,
                            e.target.value,
                            item.id
                          )
                        }
                      />
                      <hr className="my-3" />
                      <AppColorPicker
                        title="Line Color"
                        titleCSS="font-weight-500"
                        value={item.Color || "#000"}
                        onChange={(newColor) =>
                          updateBenchmarkLines("Color", newColor, item.id)
                        }
                      />
                    </Card>
                  ))}
                  <Button
                    className="secondary"
                    outline
                    block
                    onClick={addBenchmarkLine}
                  >
                    + Add Benchmark Line
                  </Button>
                </SettingsGroup>
              </>
            )}
            <FiltersGroup
              widget={widget}
              updateSection={updateSection}
              module={module}
              disabled={!widget?.ModuleAPIName}
              disabledComponent={<NoDataSource />}
            />
          </div>
        </div>
      }
    />
  );
};

export default ChartSettings;
